/**
 * Public-site URLs for the dashboard "open live site" + "voir en ligne" links. Defaults to the
 * deployed Cloudflare hosts; override per environment via REACT_APP_PREVIEW_SITE_URL /
 * REACT_APP_PRODUCTION_SITE_URL (set to http://localhost:3050 / :3060 in local dev).
 */
const strip = (value: string) => value.replace(/\/+$/, "");

// Domaines finaux de la zone bcc.cd depuis le 14/08/2026 (custom domains sur les
// workers ; les hostnames workers.dev restent servis et les anciens domaines
// pulsarinteractive.io redirigent). Production sur www.bcc.cd, le domaine du
// référencement historique de la Banque (décision du 17/08) ; l'apex 301 vers www.
export const PREVIEW_SITE_URL = strip(process.env.REACT_APP_PREVIEW_SITE_URL || "https://preview.bcc.cd");
export const PRODUCTION_SITE_URL = strip(process.env.REACT_APP_PRODUCTION_SITE_URL || "https://www.bcc.cd");

/** Pretty host label for a site URL (e.g. "bcc.preview.pulsarinteractive.io"). */
export const siteHost = (url: string): string => {
	try {
		return new URL(url).host;
	} catch {
		return url.replace(/^https?:\/\//, "");
	}
};

/**
 * The current weekly PREVIEW access code, published once by usePreviewAccessCode(). Every link that
 * points at the preview site carries it (`?ac=…`) so an editor clicking through from the admin is
 * never stopped by the preview gate. Held at module scope so buildPublicUrl injects it for ALL
 * preview links without each call site having to thread the code through.
 */
let previewAccessCode: string | null = null;

export const setPreviewAccessCode = (code: string | null | undefined): void => {
	const trimmed = (code ?? "").trim().toUpperCase();
	previewAccessCode = trimmed ? trimmed : null;
};

const withAccessCode = (url: string): string => {
	if (!previewAccessCode) return url;
	const sep = url.includes("?") ? "&" : "?";
	return `${url}${sep}ac=${encodeURIComponent(previewAccessCode)}`;
};

/** Ajoute le code d'accès preview courant à une URL preview ARBITRAIRE. Pour les surfaces qui
 *  affichent l'hôte réel remonté par le serveur (cartes d'audience du dashboard) plutôt que la
 *  constante PREVIEW_SITE_URL, que buildPublicUrl exige pour injecter le code. */
export const appendPreviewAccessCode = (url: string): string => withAccessCode(url);

/** Join a public base URL with a content path (handles leading slashes; empty path → the base).
 *  Preview-site links automatically carry the weekly access code (see {@link setPreviewAccessCode}). */
export const buildPublicUrl = (base: string, path?: string | null): string => {
	const p = (path ?? "").replace(/^\/+/, "");
	const url = p ? `${base}/${p}` : base;
	return strip(base) === PREVIEW_SITE_URL ? withAccessCode(url) : url;
};

/** Preview URL for a content path, always carrying the access code. Use where the bare
 *  PREVIEW_SITE_URL constant would otherwise be linked directly (bypassing buildPublicUrl).
 *  (buildPublicUrl already injects the code for the preview base : no double-append.) */
export const buildPreviewUrl = (path?: string | null): string =>
	buildPublicUrl(PREVIEW_SITE_URL, path);
